import React, { useEffect, useState } from 'react';
import { Table, Card, Typography, Tag, Space, Button, message, Popconfirm, Tabs, Spin, Statistic, Row, Col } from 'antd';
import { ReloadOutlined, DeleteOutlined, WarningOutlined, DatabaseOutlined, HddOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
`;

const Maintenance: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('notifications');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [databaseSizeLoading, setDatabaseSizeLoading] = useState(false);
  const [databaseSize, setDatabaseSize] = useState<any>(null);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchDeletedNotifications();
    } else if (activeTab === 'database') {
      fetchDatabaseSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, activeTab]);

  const fetchDatabaseSize = async () => {
    try {
      setDatabaseSizeLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_MPC_SETTINGS);
      if (response.data?.status === 'success' && response.data?.data?.databaseSize) {
        setDatabaseSize(response.data.data.databaseSize);
      }
    } catch (error: any) {
      console.error('Error fetching database size:', error);
      message.error(error.response?.data?.message || 'Failed to fetch database size');
    } finally {
      setDatabaseSizeLoading(false);
    }
  };

  const fetchDeletedNotifications = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
        status: 'DELETED',
        adminScope: 'ALL',
      };

      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_CRITICAL_NOTIFICATIONS,
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setNotifications(data.itemsList || []);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              (data.itemsList || []).length,
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching deleted notifications:', error);
      message.error(
        error.response?.data?.message ||
          'Failed to fetch deleted notifications'
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async (id: string) => {
    try {
      // This will be a hard delete endpoint - for now using the existing delete endpoint
      // In the future, we'll add a permanent delete endpoint
      await apiClient.delete(
        API_ENDPOINTS.ADMIN_DELETE_CRITICAL_NOTIFICATION.replace('{id}', id)
      );
      message.success('Notification permanently deleted');
      fetchDeletedNotifications();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to permanently delete notification'
      );
    }
  };

  const handleDeleteAllForever = async () => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN_DELETE_ALL_NOTIFICATIONS_FOREVER);
      message.success('All deleted notifications permanently removed');
      fetchDeletedNotifications();
    } catch (error: any) {
      console.error('Failed to delete all notifications:', error);
      message.error(error.response?.data?.message || 'Failed to delete all notifications');
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const notificationColumns = [
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity: string) => {
        const colorMap: Record<string, string> = {
          LOW: 'blue',
          MEDIUM: 'orange',
          HIGH: 'red',
          CRITICAL: 'red',
        };
        return <Tag color={colorMap[severity] || 'default'}>{severity}</Tag>;
      },
    },
    {
      title: 'Merchant Company',
      dataIndex: 'merchantCompany',
      key: 'merchantCompany',
      render: (company: any) => company?.name || 'Admin Notification',
    },
    {
      title: 'Deleted At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value: string) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Popconfirm
          title={
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Permanently delete this notification?</div>
              <div style={{ fontSize: '12px', color: '#999' }}>This action cannot be undone. The notification will be permanently removed from the database.</div>
            </div>
          }
          icon={<WarningOutlined style={{ color: 'red' }} />}
          onConfirm={() => handlePermanentDelete(record.id)}
          okText="Yes, Delete Forever"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            size="small"
          >
            Delete Forever
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const tableColumns = [
    {
      title: 'Table Name',
      dataIndex: 'table',
      key: 'table',
      render: (value: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{value}</span>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'sizeFormatted',
      key: 'sizeFormatted',
      width: 120,
      render: (value: string, record: any) => (
        <Tag color="blue">{value || formatBytes(record.sizeBytes)}</Tag>
      ),
    },
    {
      title: 'Rows',
      dataIndex: 'rowCount',
      key: 'rowCount',
      width: 100,
      render: (value: number) => (
        <span style={{ color: COLORS.accent }}>{(value || 0).toLocaleString()}</span>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'notifications',
      label: 'Deleted Notifications',
      children: (
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <div style={{
            marginBottom: 12,
            color: COLORS.textSecondary,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>Total {pagination.total} deleted notifications</div>
            <div>
              <Popconfirm
                title={
                  <div>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Permanently delete all deleted notifications?</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>This action cannot be undone. All deleted notifications will be permanently removed from the database.</div>
                  </div>
                }
                icon={<WarningOutlined style={{ color: 'red' }} />}
                onConfirm={handleDeleteAllForever}
                okText="Yes, Delete All Forever"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button type="primary" danger icon={<DeleteOutlined />} size="small">
                  Delete All Forever
                </Button>
              </Popconfirm>
            </div>
          </div>
          <Table
            rowKey="id"
            loading={loading}
            columns={notificationColumns}
            dataSource={notifications}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} notifications
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size="small"
          />
        </Card>
      ),
    },
    {
      key: 'database',
      label: (
        <span>
          <DatabaseOutlined style={{ marginRight: 8 }} />
          Database Tables (Aso Rock)
        </span>
      ),
      children: (
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          {databaseSizeLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: COLORS.textSecondary }}>
                Loading database info from Aso Rock...
              </div>
            </div>
          ) : databaseSize?.orchestrator ? (
            <div>
              {/* Overview Stats */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  <Card
                    size="small"
                    style={{
                      background: COLORS.bgPrimary,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <Statistic
                      title={<span style={{ color: COLORS.textSecondary }}>Database Name</span>}
                      value={databaseSize.orchestrator.databaseName || 'N/A'}
                      valueStyle={{ color: COLORS.accent, fontSize: '16px' }}
                      prefix={<HddOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card
                    size="small"
                    style={{
                      background: COLORS.bgPrimary,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <Statistic
                      title={<span style={{ color: COLORS.textSecondary }}>Total Size</span>}
                      value={databaseSize.orchestrator.sizeFormatted || formatBytes(databaseSize.orchestrator.sizeBytes || 0)}
                      valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                      prefix={<DatabaseOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card
                    size="small"
                    style={{
                      background: COLORS.bgPrimary,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <Statistic
                      title={<span style={{ color: COLORS.textSecondary }}>Table Count</span>}
                      value={databaseSize.orchestrator.tableCount || 0}
                      valueStyle={{ color: COLORS.textPrimary, fontSize: '16px' }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Largest Tables */}
              <div style={{ marginBottom: 16 }}>
                <Title level={5} style={{ color: COLORS.textPrimary, marginBottom: 16 }}>
                  <DatabaseOutlined style={{ marginRight: 8 }} />
                  Largest Tables
                </Title>
                <Table
                  rowKey="table"
                  columns={tableColumns}
                  dataSource={databaseSize.orchestrator.largestTables || []}
                  pagination={false}
                  size="small"
                  style={{ background: 'transparent' }}
                />
              </div>

              <div style={{ marginTop: 16, color: COLORS.textSecondary, fontSize: '12px' }}>
                Data synced from Aso Rock via the "Update Runtime to Admin" cron job.
                Large tables may need periodic maintenance (e.g., OrchestratorSessions is auto-cleaned daily).
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
              <DatabaseOutlined style={{ fontSize: 48, opacity: 0.5, marginBottom: 16 }} />
              <div>No database size data available.</div>
              <div style={{ fontSize: '12px', marginTop: 8 }}>
                Make sure the "Update Runtime to Admin" cron job is enabled in Aso Rock.
              </div>
              <Button
                type="primary"
                style={{ marginTop: 16 }}
                onClick={fetchDatabaseSize}
                icon={<ReloadOutlined />}
              >
                Retry
              </Button>
            </div>
          )}
        </Card>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Maintenance
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              if (activeTab === 'notifications') {
                fetchDeletedNotifications();
              } else if (activeTab === 'database') {
                fetchDatabaseSize();
              }
            }}
          >
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ color: COLORS.textPrimary }}
        />
      </Card>
    </PageContainer>
  );
};

export default Maintenance;

